import { FC, useMemo, useState } from "react";
import { Box, Button, CardNotification, Heading, Text, toast } from "@foris/avocado-suite";
import { useAsyncAction } from "@common/hooks/useAsyncAction";
import { downloadBlob } from "@utils/downloadBlob";
import { type LocalesByCode } from "@utils/i18nCatalog";
import { OVERRIDES_FILE_NAME, parseCatalogFile } from "@utils/i18nOverrides";
import FileUploadButton from "../file-upload-button";
import StatList from "../stat-list";
import { SETTING_NAME } from "../../constants";
import styles from "../../i18nCatalog.module.scss";

const ERROR_MESSAGE =
  "No se pudo leer el archivo. Verifica que sea el .xlsx descargado desde esta página.";

/** Step 2: turns the catalog the client filled in into the JSON the setting takes. */
const OverridesSection: FC<{ locales: LocalesByCode }> = ({ locales }) => {
  const [fileName, setFileName] = useState<string>(null);

  const { run, isLoading, error, result } = useAsyncAction(
    (file: File) => parseCatalogFile(file, locales),
    ERROR_MESSAGE,
  );

  const overridesJson = useMemo(
    () => (result ? JSON.stringify(result.overrides, null, 2) : ""),
    [result],
  );

  const hasOverrides = !!result && result.stats.esCount + result.stats.enCount > 0;

  const handleFile = (file: File) => {
    setFileName(file.name);
    run(file);
  };

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(overridesJson);
      toast.success("JSON copiado al portapapeles", { autoClose: 5000 });
    } catch (caught) {
      console.error("[i18n-catalog] Error copiando el JSON:", caught);
      toast.error("No se pudo copiar. Selecciona el texto y cópialo manualmente.", {
        autoClose: 5000,
      });
    }
  };

  return (
    <Box className={styles.i18nCatalog_card}>
      <div className={styles.i18nCatalog_cardHeader}>
        <Heading type="h3">2. Convertir el Excel a JSON</Heading>
        <Text type="sm">
          Convierte el archivo que devolvió el cliente en el JSON que se le pasa al equipo de
          backend.
        </Text>
      </div>

      <FileUploadButton
        fileName={fileName}
        isLoading={isLoading}
        label="Seleccionar archivo"
        onFile={handleFile}
      />

      {!!error && (
        <CardNotification state="error" title="Error" outlined>
          <Text type="sm">{error}</Text>
        </CardNotification>
      )}

      {!!result && (
        <>
          <StatList
            items={[
              { label: "overrides ES", value: result.stats.esCount },
              { label: "overrides EN", value: result.stats.enCount },
              { label: "sin cambios", value: result.stats.unchangedCount },
              { label: "descartados", value: result.stats.skippedCount },
            ]}
          />

          <div className={styles.i18nCatalog_notifications}>
            {hasOverrides && (
              <CardNotification
                state="success"
                title={`${result.stats.esCount + result.stats.enCount} overrides listos`}
                outlined
              >
                <Text type="sm">Copia el JSON de abajo y pégalo en el setting {SETTING_NAME}.</Text>
              </CardNotification>
            )}

            {!!result.warnings.length && (
              <CardNotification
                state="warning"
                title={`${result.warnings.length} filas descartadas`}
                outlined
              >
                <ul className={styles.i18nCatalog_itemList}>
                  {result.warnings.map(warning => (
                    <li
                      className={styles.i18nCatalog_item}
                      key={`${warning.locale}-${warning.sheetName}-${warning.rowNumber}-${warning.fullKey}`}
                    >
                      <Text type="sm">{warning.message}</Text>
                      <Text className={styles.i18nCatalog_itemLocation} type="xs">
                        Pestaña &quot;{warning.sheetName}&quot;, fila {warning.rowNumber}
                      </Text>
                    </li>
                  ))}
                </ul>
              </CardNotification>
            )}

            {!hasOverrides && (
              <CardNotification state="info" title="No hay cambios que aplicar" outlined>
                <Text type="sm">
                  El archivo no trae textos nuevos válidos en las columnas &quot;Nuevo texto&quot;.
                </Text>
              </CardNotification>
            )}
          </div>

          {hasOverrides && (
            <>
              <pre className={styles.i18nCatalog_json}>{overridesJson}</pre>

              <div className={styles.i18nCatalog_actions}>
                <Button leftIcon="copy" onClick={handleCopyJson} variant="primary">
                  Copiar JSON
                </Button>
                <Button
                  leftIcon="download"
                  onClick={() =>
                    downloadBlob(
                      new Blob([overridesJson], { type: "application/json" }),
                      OVERRIDES_FILE_NAME,
                    )
                  }
                  variant="secondary"
                >
                  Descargar JSON
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default OverridesSection;
